// web/src/admin/AdminCustomerDetail.tsx (/admin/customers/:orgId) — one org's full picture
// (50_ADMIN_CONSOLE.md §3.3): plan and kill-switch header, credits with the whole `credit_ledger`,
// spend per model and per request, requests with their complexity audit trail, sites, and the
// three permitted actions.
//
// ADMIN-ONLY, all of it: dollars, DEPLOYMENT names, gate check names, `complexity` and
// `complexityReason` (02 §8, doc 10 §8, doc 20 §8). None of these fields exists on any
// customer-facing DTO and this screen is behind AdminGate + the API's AdminGuard.
//
// :orgId IS AN ORG ID, not a Stripe customer id — the pre-doc-50 version of this screen took the
// latter. The Stripe/Connect view is one click away via `stripeCustomerUrl` when the org has one.
import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { age, dateOf } from '../lib/status';
import { PageHeader } from '../components/PageHeader';
import { AsyncBoundary } from '../components/AsyncBoundary';
import { Skeleton } from '../components/Skeleton';
import { KPICard } from '../components/KPICard';
import { Icon } from '../components/Icon';
import { Modal } from '../components/Modal';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { toast } from '../components/Toast';
import { FEATURES } from '../lib/features';
import { WindowToggle, credits, marginTone, pct, usd, useAdminWindow } from './adminShared';

/** doc 12 Phase B's ledger vocabulary, in KC's words rather than the column's. */
const LEDGER_LABEL: Record<string, string> = {
  plan_renewal: 'Plan credits',
  topup: 'Top-up purchased',
  admin_grant: 'Granted by admin',
  request: 'Update charged',
  cycle_reset: 'Cycle reset',
};

export function AdminCustomerDetail() {
  const { id = '' } = useParams();
  const [days, setDays] = useAdminWindow();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['admin', 'org', id, days], queryFn: () => api.adminOrg(id, days) });
  const [grantOpen, setGrantOpen] = useState(false);
  const [grantAmount, setGrantAmount] = useState('100');
  const [grantNote, setGrantNote] = useState('');
  const [pauseOpen, setPauseOpen] = useState(false);

  const refresh = () => qc.invalidateQueries({ queryKey: ['admin'] });

  async function doGrant() {
    const n = Number(grantAmount);
    if (!Number.isInteger(n) || n <= 0) {
      toast({ kind: 'error', message: 'Enter a whole number of credits.' });
      return;
    }
    try {
      const r = await api.adminGrantCredits(id, n, grantNote || undefined);
      setGrantOpen(false);
      setGrantNote('');
      toast({ kind: 'success', message: `Granted ${n} credits — ${r.creditsRemaining} now available.` });
      refresh();
    } catch {
      toast({ kind: 'error', message: "Couldn't grant credits." });
    }
  }

  async function doPause(paused: boolean) {
    try {
      await api.adminSetPaused(id, paused, paused ? 'Paused from the admin console' : undefined);
      setPauseOpen(false);
      toast({
        kind: 'success',
        message: paused ? 'Account paused — all model calls blocked.' : 'Account resumed.',
      });
      refresh();
    } catch {
      toast({ kind: 'error', message: "Couldn't change the pause state." });
    }
  }

  async function doRetry(requestId: string) {
    try {
      await api.adminRetryRequest(requestId);
      toast({ kind: 'success', message: 'Retrying — the site is unblocked.' });
      refresh();
    } catch {
      toast({ kind: 'error', message: "Couldn't retry that request." });
    }
  }

  return (
    <AsyncBoundary query={q} skeleton={<Skeleton variant="card" count={3} />}>
      {(d) => {
        const o = d.org;
        return (
          <>
            <Link
              to="/admin/customers"
              className="mb-4 inline-flex items-center gap-1 text-sm text-muted hover:text-ink"
            >
              <Icon name="arrow-left" size={16} />
              Customers
            </Link>
            <PageHeader
              title={o.name}
              subtitle={`${o.tier ?? 'no plan'} · ${o.subStatus} · ${o.sites} site${o.sites === 1 ? '' : 's'}`}
            />

            {o.killed && (
              <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-danger bg-danger/10 px-4 py-3 text-sm">
                <Icon name="lock" size={16} className="text-danger" />
                <span className="text-ink">
                  Paused — every model call for this org is blocked, in flight included.
                </span>
                <span className="text-xs text-muted">{d.killedReason}</span>
              </div>
            )}
            {o.unprovisioned && (
              // 90_OPEN_ITEMS D-3: nothing provisions org_budgets at signup. `ensureBudget()`
              // fills it in lazily on first touch, so this state is real but short-lived.
              <div className="mb-4 rounded-lg border border-hair bg-surface/40 px-4 py-3 text-xs text-muted">
                No credit budget row yet — one is created automatically on this org’s first
                request, or by granting credits below (90_OPEN_ITEMS D-3).
              </div>
            )}

            <div className="mb-4 flex flex-wrap items-center gap-2">
              <WindowToggle days={days} onChange={setDays} />
              <button
                onClick={() => setGrantOpen(true)}
                className="inline-flex min-h-11 items-center gap-2 rounded-md bg-run px-4 text-sm font-medium text-runink"
              >
                <Icon name="wallet" size={15} />
                Grant credits
              </button>
              <button
                onClick={() => setPauseOpen(true)}
                className={`inline-flex min-h-11 items-center gap-2 rounded-md border px-4 text-sm ${
                  o.killed ? 'border-success text-success' : 'border-danger text-danger'
                }`}
              >
                <Icon name={o.killed ? 'check' : 'lock'} size={15} />
                {o.killed ? 'Resume' : 'Pause'}
              </button>
              {d.stripeCustomerUrl && (
                <a
                  href={d.stripeCustomerUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex min-h-11 items-center gap-2 rounded-md border border-hair px-4 text-sm text-ink"
                >
                  <Icon name="external" size={15} />
                  Stripe
                </a>
              )}
            </div>

            <section className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
              <KPICard
                label="Margin"
                value={usd(o.marginCents)}
                delta={pct(o.marginPct)}
                accent={o.marginCents < 0 ? 'danger' : 'run'}
              />
              <KPICard label="MRR" value={usd(o.mrrCents)} accent="info" />
              <KPICard label={`AI spend (${days}d)`} value={usd(o.aiSpendCents)} accent="info" />
              <KPICard
                label="Rework"
                value={pct(o.reworkPct)}
                delta={usd(o.reworkCents)}
                accent={(o.reworkPct ?? 0) > 0.2 ? 'warn' : 'info'}
              />
            </section>

            {/* ── Credits ─────────────────────────────────────────────────────────────────── */}
            <section className="mb-6">
              <h2 className="mb-3 font-head text-sm text-muted">Credits</h2>
              <div className="mb-3 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <KPICard label="Used" value={credits(o.creditsUsed)} accent="info" />
                <KPICard label="Available" value={credits(o.creditsGranted)} accent="info" />
                <KPICard
                  label="Utilisation"
                  value={pct(o.utilisationPct)}
                  accent={(o.utilisationPct ?? 0) > 0.8 ? 'warn' : 'info'}
                />
                <KPICard
                  label="Rollover"
                  value={credits(d.creditsRollover)}
                  delta={d.cycleEnd ? `resets ${dateOf(d.cycleEnd)}` : undefined}
                  accent="info"
                />
              </div>
              <ul className="flex flex-col gap-1.5">
                {d.ledger.map((l) => (
                  <li
                    key={l.id}
                    className="flex items-center justify-between gap-3 rounded-md border border-hair bg-surface px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 truncate text-ink">
                      {LEDGER_LABEL[l.reason] ?? l.reason}
                      {l.requestId && <span className="ml-2 text-xs text-muted">on an update</span>}
                    </span>
                    <span className="flex shrink-0 items-center gap-3 text-xs text-muted">
                      <span>{dateOf(l.createdAt)}</span>
                      <span className={`font-head ${l.delta >= 0 ? 'text-success' : 'text-ink'}`}>
                        {l.delta >= 0 ? '+' : ''}
                        {credits(l.delta)}
                      </span>
                      <span className="hidden sm:inline">bal {credits(l.balanceAfter)}</span>
                    </span>
                  </li>
                ))}
                {d.ledger.length === 0 && (
                  <li className="rounded-md border border-hair bg-surface/40 px-3 py-6 text-center text-sm text-muted">
                    No ledger entries yet.
                  </li>
                )}
              </ul>
            </section>

            {/* ── Spend by model ──────────────────────────────────────────────────────────── */}
            <section className="mb-6">
              <h2 className="mb-3 font-head text-sm text-muted">Spend by model</h2>
              <ul className="flex flex-col gap-1.5">
                {d.byModel.map((m) => (
                  <li
                    key={m.model}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-hair bg-surface px-3 py-2 text-sm"
                  >
                    <span className="font-head text-ink">{m.model}</span>
                    <span className="flex items-center gap-3 text-xs text-muted">
                      <span>{m.calls} calls</span>
                      <span>rework {usd(m.reworkCents)}</span>
                      <b className="font-head text-ink">{usd(m.cents)}</b>
                    </span>
                  </li>
                ))}
                {d.byModel.length === 0 && (
                  <li className="rounded-md border border-hair bg-surface/40 px-3 py-6 text-center text-sm text-muted">
                    No model calls in this window.
                  </li>
                )}
              </ul>
            </section>

            {/* ── Requests: the complexity audit trail (D-6) + per-request spend ──────────── */}
            <section className="mb-6">
              <h2 className="mb-3 font-head text-sm text-muted">Requests</h2>
              <ul className="flex flex-col gap-2">
                {d.requests.map((r) => (
                  <li key={r.id} className="rounded-lg border border-hair bg-surface p-3">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <span className="min-w-0">
                        <span className="block truncate font-medium text-ink">
                          {r.displayNo != null && (
                            <span className="mr-1.5 font-head text-xs text-muted">#{r.displayNo}</span>
                          )}
                          {r.title}
                        </span>
                        <span className="block truncate text-xs text-muted">
                          {r.siteName ?? '—'} · {r.status.replace(/_/g, ' ')} · {dateOf(r.createdAt)}
                        </span>
                      </span>
                      <span className="shrink-0 text-right">
                        <span className="block font-head text-sm text-ink">{usd(r.aiSpendCents)}</span>
                        <span className="block text-[11px] text-muted">
                          {r.creditsCharged == null ? 'unbilled' : `${credits(r.creditsCharged)} credits`}
                        </span>
                      </span>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[11px]">
                      {/* D-6: the verdict AND the rationale. This pair is the only audit trail
                          the Opus-tier spend has (02 §9) — without it a drifting classifier is
                          invisible until the bill arrives. */}
                      {r.complexity && (
                        <span
                          className={`rounded-pill border px-1.5 py-0.5 ${
                            r.complexity === 'complex' ? 'border-warn text-warn' : 'border-hair text-muted'
                          }`}
                          title={r.complexityReason ?? undefined}
                        >
                          {r.complexity}
                        </span>
                      )}
                      {r.models.map((m) => (
                        <span key={m} className="rounded-pill border border-hair px-1.5 py-0.5 text-muted">
                          {m}
                        </span>
                      ))}
                      {r.overCap && (
                        <span className="rounded-pill border border-danger px-1.5 py-0.5 text-danger">
                          over cap
                        </span>
                      )}
                      {r.reworkCents > 0 && (
                        <span className="rounded-pill border border-hair px-1.5 py-0.5 text-muted">
                          rework {usd(r.reworkCents)}
                        </span>
                      )}
                      {r.gateGreen === false && (
                        <span className="rounded-pill border border-warn px-1.5 py-0.5 text-warn">
                          gate: {r.gateFailures.join(', ') || 'red'}
                        </span>
                      )}
                      {FEATURES.chalice && r.status === 'needs_attention' && ( // retry re-runs chalice: hidden (portal spec 10)
                        <button
                          onClick={() => doRetry(r.id)}
                          className="inline-flex min-h-9 items-center gap-1 rounded-md border border-warn px-2 text-warn"
                        >
                          <Icon name="refresh" size={12} />
                          Retry
                        </button>
                      )}
                    </div>
                    {r.complexityReason && (
                      <p className="mt-1.5 text-[11px] italic text-muted">{r.complexityReason}</p>
                    )}
                  </li>
                ))}
                {d.requests.length === 0 && (
                  <li className="rounded-md border border-hair bg-surface/40 px-3 py-6 text-center text-sm text-muted">
                    No requests in this window.
                  </li>
                )}
              </ul>
            </section>

            {/* ── Sites ───────────────────────────────────────────────────────────────────── */}
            <section>
              <h2 className="mb-3 font-head text-sm text-muted">Sites</h2>
              <ul className="flex flex-col gap-1.5">
                {d.sites.map((s) => (
                  <li
                    key={s.id}
                    className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-hair bg-surface px-3 py-2 text-sm"
                  >
                    <span className="min-w-0 truncate text-ink">{s.name}</span>
                    <span className="flex items-center gap-3 text-xs text-muted">
                      <span>{s.state}</span>
                      <span>{s.awsRegion ?? 'no region'}</span>
                      <span>{s.lastPublish ? `published ${age(s.lastPublish)} ago` : 'never published'}</span>
                      {s.url && (
                        <a href={s.url} target="_blank" rel="noreferrer" className="text-ink">
                          <Icon name="external" size={14} />
                        </a>
                      )}
                    </span>
                  </li>
                ))}
                {d.sites.length === 0 && (
                  <li className="rounded-md border border-hair bg-surface/40 px-3 py-6 text-center text-sm text-muted">
                    No sites.
                  </li>
                )}
              </ul>
            </section>

            <Modal open={grantOpen} onClose={() => setGrantOpen(false)} title="Grant credits" size="sm">
              <p className="mb-3 text-sm text-muted">
                Granted credits behave like purchased ones: they roll over and survive the cycle
                reset (02 §9). Recorded in the ledger as <b>admin_grant</b> and in the audit log.
              </p>
              <label className="mb-3 block text-xs text-muted">
                Credits
                <input
                  type="number"
                  min={1}
                  step={1}
                  value={grantAmount}
                  onChange={(e) => setGrantAmount(e.target.value)}
                  className="mt-1 min-h-11 w-full rounded-md border border-hair bg-surface px-3 text-sm text-ink"
                />
              </label>
              <label className="block text-xs text-muted">
                Note (optional)
                <input
                  value={grantNote}
                  onChange={(e) => setGrantNote(e.target.value)}
                  placeholder="why"
                  className="mt-1 min-h-11 w-full rounded-md border border-hair bg-surface px-3 text-sm text-ink"
                />
              </label>
              <div className="mt-5 flex justify-end gap-2">
                <button
                  onClick={() => setGrantOpen(false)}
                  className="min-h-11 rounded-md border border-hair px-4 text-sm text-ink"
                >
                  Cancel
                </button>
                <button
                  onClick={doGrant}
                  className="min-h-11 rounded-md bg-run px-4 text-sm font-medium text-runink"
                >
                  Grant
                </button>
              </div>
            </Modal>

            <ConfirmDialog
              open={pauseOpen}
              title={o.killed ? 'Resume this account?' : 'Pause this account?'}
              body={
                o.killed
                  ? 'Model calls for this org start working again immediately.'
                  : 'This blocks every model call for the org, including passes already running. It is an incident tool, not the credit cap.'
              }
              confirmLabel={o.killed ? 'Resume' : 'Pause'}
              destructive={!o.killed}
              onConfirm={() => doPause(!o.killed)}
              onCancel={() => setPauseOpen(false)}
            />
          </>
        );
      }}
    </AsyncBoundary>
  );
}
